/**
 * Authorities List Page
 *
 * Lists all authorities the current employee has access to.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Business as BusinessIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useCryptoKeyStore } from '@antragsmanager/crypto-lib';
import { getMyAuthorities } from '../features/encryption/api/keysApi';
import { AuthoritySetupWizard } from '../features/encryption/components/AuthoritySetup/AuthoritySetupWizard';

export function AuthoritiesListPage() {
  const [showSetupWizard, setShowSetupWizard] = useState(false);
  const navigate = useNavigate();
  const { activeKey } = useCryptoKeyStore();

  const {
    data: authorities,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['my-authorities', activeKey?.keyId],
    queryFn: () => getMyAuthorities(activeKey!.keyId),
    enabled: !!activeKey?.keyId,
  });

  const handleSetupComplete = () => {
    setShowSetupWizard(false);
    refetch();
  };

  if (!activeKey) {
    return (
      <Alert severity="warning">
        <Typography variant="body2">
          Sie müssen zuerst einen Schlüssel erstellen, bevor Sie Behörden verwalten können.
        </Typography>
        <Button
          variant="contained"
          size="small"
          sx={{ mt: 1 }}
          onClick={() => navigate('/admin/keys')}
        >
          Schlüssel erstellen
        </Button>
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Fehler beim Laden der Behörden: {error instanceof Error ? error.message : 'Unbekannter Fehler'}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Meine Behörden</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowSetupWizard(true)}
          data-testid="create-authority-button"
        >
          Neue Behörde erstellen
        </Button>
      </Box>

      {authorities && authorities.length > 0 ? (
        <Card>
          <List>
            {authorities.map((authority) => (
              <ListItem
                key={authority.authorityId}
                divider
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/admin/authorities/${authority.authorityId}`)}
              >
                <BusinessIcon sx={{ mr: 2, color: 'primary.main' }} />
                <ListItemText
                  primary={authority.authorityName}
                  secondary={
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                      <Chip
                        label={authority.isActive ? 'Aktiv' : 'Inaktiv'}
                        size="small"
                        color={authority.isActive ? 'success' : 'default'}
                      />
                      <Chip
                        label={`Epoche ${authority.currentEpoch}`}
                        size="small"
                        variant="outlined"
                        data-testid="epoch-badge"
                      />
                      <Chip
                        label={`${authority.activeEmployeeCount} Mitarbeiter`}
                        size="small"
                        variant="outlined"
                        data-testid="employee-count"
                      />
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <ChevronRightIcon color="action" />
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Card>
      ) : (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <BusinessIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Keine Behörden gefunden
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Sie sind noch keiner Behörde zugeordnet. Erstellen Sie eine neue Behörde oder
              lassen Sie sich von einem bestehenden Mitarbeiter einladen.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowSetupWizard(true)}
            >
              Behörde erstellen
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Authority Setup Wizard Dialog */}
      <AuthoritySetupWizard
        open={showSetupWizard}
        onClose={() => setShowSetupWizard(false)}
        onComplete={(authorityId) => {
          handleSetupComplete();
          navigate(`/admin/authorities/${authorityId}`);
        }}
      />
    </Box>
  );
}
